import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useTheme } from '@/lib/theme-store';
import { toggleTheme } from '@/lib/theme-store';

// `external` means the route is served by the terminal app, not by this SPA's
// router. Those need a real <a> so the browser actually leaves the bundle; a
// react-router <Link> would look the path up here, fail to match, and render
// the 404 page without ever making a request.
//
// Record is external on purpose: this nav promises a track record, and the real
// scored ledger is a better thing to hand someone than a page describing it.
const NAV: { to: string; label: string; external?: boolean }[] = [
  { to: '/platform', label: 'Platform' },
  { to: '/engine', label: 'Engine' },
  { to: '/data', label: 'Data' },
  { to: '/record', label: 'Record', external: true },
  { to: '/design', label: 'Design' },
  { to: '/access', label: 'Access' },
  { to: '/faq', label: 'FAQ' },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // The bar condenses and frosts once the page has moved, so it stops competing
  // with the hero but never disappears.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Route change closes the drawer; otherwise it stays open over the new page.
  useEffect(() => setOpen(false), [pathname]);

  // Escape closes, and focus returns to the control that opened it.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      toggleRef.current?.focus();
    };

    // Focus trap: the drawer covers the page, so Tab must not reach behind it.
    const onFocus = (e: FocusEvent) => {
      const panel = panelRef.current;
      if (panel && e.target instanceof Node && !panel.contains(e.target)) {
        panel.querySelector<HTMLElement>('a, button')?.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocus);
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector<HTMLElement>('a, button')?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocus);
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="site-header__inner shell">
        <Link to="/" className="wordmark" aria-label="World Finance Terminal, home">
          {/* The mark is decorative here: the adjacent text already names the
              site, and the link carries its own aria-label. */}
          <img
            className="wordmark__mark"
            src="/logo.png"
            alt=""
            width={34}
            height={34}
            decoding="async"
          />
          <span className="wordmark__text">
            World Finance Terminal<span className="wordmark__sub">by GodEngine</span>
          </span>
        </Link>

        <nav className="site-nav" aria-label="Primary">
          {NAV.map((item) =>
            item.external ? (
              <a key={item.to} href={item.to} className="site-nav__link">
                <span>{item.label}</span>
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `site-nav__link ${isActive ? 'is-active' : ''}`}
              >
                <span>{item.label}</span>
              </NavLink>
            )
          )}
        </nav>

        <div className="site-header__actions">
          <button
            type="button"
            className="icon-btn"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'terminal' ? 'light' : 'dark'} theme`}
          >
            {theme === 'terminal' ? '☾' : '☀'}
          </button>

          <a href="/cockpit" className="btn btn--primary btn--sm">
            Open terminal
          </a>

          <button
            ref={toggleRef}
            type="button"
            className={`burger ${open ? 'is-open' : ''}`}
            aria-expanded={open}
            aria-controls="site-drawer"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>

      <div
        id="site-drawer"
        ref={panelRef}
        className={`drawer ${open ? 'is-open' : ''}`}
        hidden={!open}
      >
        <nav className="drawer__nav" aria-label="Mobile">
          {NAV.map((item, i) => {
            const Tag = (item.external ? 'a' : Link) as React.ElementType
            const linkProps = item.external ? { href: item.to } : { to: item.to }
            return (
            <Tag
              key={item.to}
              {...linkProps}
              className="drawer__link"
              style={{ ['--i' as string]: i }}
            >
              {/* Decorative numbering — kept out of the accessible name so the
                  link announces as "Platform", not "01 Platform". */}
              <span className="drawer__index" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              {item.label}
            </Tag>
            )
          })}
          <a
            href="/cockpit"
            className="drawer__link drawer__link--cta"
            style={{ ['--i' as string]: NAV.length }}
          >
            <span className="drawer__index" aria-hidden="true">
              →
            </span>
            Open terminal
          </a>
        </nav>
      </div>
    </header>
  );
}
